// Proposals inbox (design §3.4, §10): agent writes in `proposal` folders
// wait here. Each shows its diff against the base revision and can be
// approved or rejected with an optional note.

import { useState, type ReactNode } from 'react'
import { useDialogs } from '../dialogs'
import { EmptyState } from '../ui'
import { KbDiff } from './kb-diff'
import { KbAuthorLine } from './kb-page-view'
import type { KbProposal, KbSpaceLabel } from './kb-types'

export interface KbProposalsProps {
  proposals: KbProposal[]
  spaceLabel: KbSpaceLabel
  /**
   * The changes view for an opened proposal — the parent loads the base
   * revision's text (`baseRev`) and renders a diff. Default: a diff against
   * an empty page (fine for new pages).
   */
  renderChanges?: (proposal: KbProposal) => ReactNode
  /** Hide approve/reject (no `kb.review`). */
  readOnly?: boolean
  onApprove: (proposal: KbProposal, note: string | undefined) => void
  onReject: (proposal: KbProposal, note: string | undefined) => void
  /** Open the target page. */
  onOpenPage?: (proposal: KbProposal) => void
  /** Id of the proposal whose decision is being submitted. */
  decidingId?: string | null
}

export function KbProposals({
  proposals,
  spaceLabel,
  renderChanges,
  readOnly,
  onApprove,
  onReject,
  onOpenPage,
  decidingId,
}: KbProposalsProps) {
  if (proposals.length === 0) {
    return (
      <EmptyState title="No proposals waiting">
        Agent changes to folders that need review show up here.
      </EmptyState>
    )
  }
  return (
    <ul className="space-y-3">
      {proposals.map((proposal) => (
        <ProposalItem
          key={proposal.id}
          proposal={proposal}
          spaceLabel={spaceLabel}
          renderChanges={renderChanges}
          readOnly={readOnly}
          deciding={decidingId === proposal.id}
          disabled={decidingId != null}
          onApprove={onApprove}
          onReject={onReject}
          onOpenPage={onOpenPage}
        />
      ))}
    </ul>
  )
}

function ProposalItem({
  proposal,
  spaceLabel,
  renderChanges,
  readOnly,
  deciding,
  disabled,
  onApprove,
  onReject,
  onOpenPage,
}: {
  proposal: KbProposal
  spaceLabel: KbSpaceLabel
  renderChanges?: (proposal: KbProposal) => ReactNode
  readOnly?: boolean
  deciding: boolean
  disabled: boolean
  onApprove: KbProposalsProps['onApprove']
  onReject: KbProposalsProps['onReject']
  onOpenPage?: KbProposalsProps['onOpenPage']
}) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const outdated =
    !proposal.isNewPage &&
    proposal.baseRev != null &&
    proposal.currentRev !== proposal.baseRev
  const trimmedNote = note.trim() || undefined
  const { confirm } = useDialogs()

  async function confirmReject(): Promise<void> {
    const ok = await confirm({
      kind: 'warning',
      title: `Reject the proposal for “${proposal.title ?? proposal.path}”?`,
      message: 'The agent’s change is not applied. It stays visible in the page history as rejected.',
      confirmLabel: 'Reject',
    })
    if (ok) onReject(proposal, trimmedNote)
  }

  return (
    <li className="panel">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
        <div className="min-w-0">
          <p className="text-sm text-ink-3">
            {spaceLabel(proposal.space)} /{' '}
            {onOpenPage && !proposal.isNewPage ? (
              <button
                type="button"
                className="font-mono text-ink-2 hover:text-ink hover:underline"
                onClick={() => onOpenPage(proposal)}
              >
                {proposal.path}
              </button>
            ) : (
              <span className="font-mono text-ink-2">{proposal.path}</span>
            )}
            {proposal.isNewPage ? (
              <span className="ml-2 rounded-full bg-info-soft px-2 text-xs font-medium text-info">
                new page
              </span>
            ) : null}
          </p>
          <p className="mt-0.5 font-display text-md font-semibold text-ink">{proposal.title}</p>
          {proposal.message ? (
            <p className="mt-0.5 text-base text-ink-2">{proposal.message}</p>
          ) : null}
          <p className="mt-1 text-sm text-ink-3">
            <KbAuthorLine author={proposal.author} at={proposal.createdAt} />
          </p>
        </div>
        <button
          type="button"
          className="btn-ghost btn-sm"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? 'Hide changes' : 'Show changes'}
        </button>
      </div>

      {open ? (
        <div className="space-y-3 border-t border-line px-5 py-4">
          {outdated ? (
            <p className="rounded-control border border-signal/30 bg-signal-soft px-3 py-2 text-sm text-ink">
              Based on rev {proposal.baseRev}; the page is now at rev {proposal.currentRev}.
              Approving replaces the newer changes.
            </p>
          ) : null}
          {renderChanges ? (
            renderChanges(proposal)
          ) : (
            <KbDiff before="" after={proposal.raw} beforeLabel="empty" afterLabel="proposal" />
          )}
        </div>
      ) : null}

      {readOnly ? null : (
      <div className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-3">
        <input
          className="field-input min-w-[12rem] flex-1"
          placeholder="Note (optional)"
          aria-label="Decision note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <button
          type="button"
          className="btn-danger-quiet"
          disabled={disabled}
          onClick={() => void confirmReject()}
        >
          Reject
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={disabled}
          onClick={() => onApprove(proposal, trimmedNote)}
        >
          {deciding ? 'Saving…' : 'Approve'}
        </button>
      </div>
      )}
    </li>
  )
}
